import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
  InputPassword,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stack,
  Textarea,
} from "@altimateai/lego";
import type { Control, UseFormWatch } from "react-hook-form";
import type { AzureDatabricksFormValues } from "./schema";

interface FieldsProps {
  control: Control<AzureDatabricksFormValues>;
  watch: UseFormWatch<AzureDatabricksFormValues>;
}

export const AzureDatabricksFields = ({ control, watch }: FieldsProps): JSX.Element => {
  const scope = watch("system_catalog_scope");

  return (
    <Stack gap="xl" direction="column">
      <div className="al-rounded-md al-border al-border-blue-200 al-bg-blue-50 al-px-4 al-py-3 al-text-sm al-text-blue-900">
        <strong>At least one credential is required:</strong> provide an Azure Tenant ID
        (Entra-managed), or a Service Principal Client ID + Secret. You may also provide all three
        if your setup uses both.
      </div>

      <FormField
        control={control}
        name="azure_tenant_id"
        render={({ field }) => (
          <FormItem>
            <Label className="al-block" htmlFor="azure_tenant_id">
              Azure Tenant ID
            </Label>
            <FormControl>
              <Input
                id="azure_tenant_id"
                placeholder="Your Entra ID Tenant ID (optional if Service Principal credentials are provided)"
                className="al-mt-1"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="databricks_account_id"
        render={({ field }) => (
          <FormItem>
            <Label className="al-block" htmlFor="databricks_account_id">
              Databricks Account ID
            </Label>
            <FormControl>
              <Input
                id="databricks_account_id"
                placeholder="Your Databricks Account ID"
                className="al-mt-1"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="workspace_url"
        render={({ field }) => (
          <FormItem>
            <Label className="al-block" htmlFor="workspace_url">
              Workspace URL
            </Label>
            <FormControl>
              <Input
                id="workspace_url"
                placeholder="https://adb-<workspace-id>.azuredatabricks.net"
                className="al-mt-1"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="sp_client_id"
        render={({ field }) => (
          <FormItem>
            <Label className="al-block" htmlFor="sp_client_id">
              Service Principal Client ID
            </Label>
            <FormControl>
              <Input
                id="sp_client_id"
                placeholder="Application (Entra ID) or Databricks-managed Client ID"
                className="al-mt-1"
                autoComplete="new-password"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="sp_secret"
        render={({ field }) => (
          <FormItem>
            <Label className="al-block" htmlFor="sp_secret">
              Service Principal Secret
            </Label>
            <FormControl>
              <InputPassword
                id="sp_secret"
                className="al-mt-1"
                autoComplete="new-password"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="sql_warehouse_id"
        render={({ field }) => (
          <FormItem>
            <Label className="al-block" htmlFor="sql_warehouse_id">
              SQL Warehouse ID
            </Label>
            <FormControl>
              <Input
                id="sql_warehouse_id"
                placeholder="warehouse_id of ALTIMATE_WH"
                className="al-mt-1"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="system_catalog_scope"
        render={({ field }) => (
          <FormItem>
            <Label className="al-block" htmlFor="system_catalog_scope">
              System Catalog Access Scope
            </Label>
            <FormControl>
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger id="system_catalog_scope" className="al-mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All system schemas granted</SelectItem>
                  <SelectItem value="specific">Specific schemas granted</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {scope === "specific" && (
        <FormField
          control={control}
          name="system_catalog_schemas"
          render={({ field }) => (
            <FormItem>
              <Label className="al-block" htmlFor="system_catalog_schemas">
                Granted Schemas
              </Label>
              <FormControl>
                <Textarea
                  id="system_catalog_schemas"
                  placeholder="billing, compute"
                  className="al-mt-1"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </Stack>
  );
};
